import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../../api/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface ChatContact {
  id: string;       // mapped from _id
  _id: string;
  name: string;
  role: string;
  avatar: string;   // initials derived from name
  color: string;    // deterministic color from name
  online: boolean;  // always true for simplicity (no WS presence)
  unread: number;
  lastMessage: string;
  lastMessageTime: string | null;
  isGroup?: boolean;
}

export interface ChatMessage {
  id: string;
  senderId: string;  // 'me' | contactId
  text: string;
  time: string;
  createdAt?: string;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
}

interface ChatState {
  contacts: ChatContact[];
  messages: Record<string, ChatMessage[]>;  // keyed by contact _id
  activeContactId: string;
  typing: Record<string, boolean>;
  loading: boolean;
  loadingMessages: boolean;
  error: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const COLORS = ['blue', 'teal', 'purple', 'amber', 'rose', 'indigo', 'green'];

function getColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return COLORS[hash % COLORS.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function formatTime(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

// ─── Auto-reply fallback (used when no real peer exists) ──────────────────────
export const AUTO_REPLIES: Record<string, string[]> = {
  default: [
    'Got it, thanks!',
    'Sure, let me check.',
    'Sounds good 👍',
    'I will get back to you shortly.',
    'Understood!',
  ],
};

// ─── Async Thunks ─────────────────────────────────────────────────────────────

// Fetch all contacts (all users except self)
export const fetchChatContacts = createAsyncThunk(
  'chat/fetchContacts',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get('/chat/contacts');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load contacts');
    }
  }
);

// Fetch messages for a conversation
export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async (contactId: string, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/chat/messages/${contactId}`);
      return { contactId, messages: res.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load messages');
    }
  }
);

// Send a message to backend
export const sendMessageAsync = createAsyncThunk(
  'chat/sendMessage',
  async ({ receiverId, text, file }: { receiverId: string; text?: string; file?: File }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('receiverId', receiverId);
      if (text) formData.append('text', text);
      if (file) formData.append('file', file);

      const res = await apiClient.post('/chat/messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to send message');
    }
  }
);

// Mark messages as read
export const markMessagesReadAsync = createAsyncThunk(
  'chat/markRead',
  async (contactId: string, { rejectWithValue }) => {
    try {
      await apiClient.put(`/chat/messages/${contactId}/read`);
      return contactId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark read');
    }
  }
);

// Delete a message
export const deleteMessageAsync = createAsyncThunk(
  'chat/deleteMessage',
  async (messageId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/chat/messages/${messageId}`);
      return messageId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete message');
    }
  }
);

// Create a group
export const createGroupAsync = createAsyncThunk(
  'chat/createGroup',
  async ({ name, members }: { name: string; members: string[] }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/chat/groups', { name, members });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create group');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────
const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    contacts: [],
    messages: {},
    activeContactId: '',
    typing: {},
    loading: false,
    loadingMessages: false,
    error: null,
  } as ChatState,
  reducers: {
    setActiveContact(state, action: PayloadAction<string>) {
      state.activeContactId = action.payload;
      // Clear unread badge locally
      const c = state.contacts.find(c => c.id === action.payload);
      if (c) c.unread = 0;
    },
    // Optimistic local message add (for instant UI feedback)
    addLocalMessage(state, action: PayloadAction<{ contactId: string; message: ChatMessage }>) {
      const { contactId, message } = action.payload;
      if (!state.messages[contactId]) state.messages[contactId] = [];
      state.messages[contactId].push(message);
    },
    receiveMessage(state, action: PayloadAction<{ contactId: string; text: string; time: string; fileUrl?: string; fileName?: string; fileType?: string; incrementUnread?: boolean }>) {
      const { contactId, text, time, fileUrl, fileName, fileType, incrementUnread } = action.payload;
      const msg: ChatMessage = {
        id: `local_${Date.now()}`,
        senderId: contactId,
        text,
        time,
        fileUrl,
        fileName,
        fileType
      };
      if (!state.messages[contactId]) state.messages[contactId] = [];
      state.messages[contactId].push(msg);

      const contact = state.contacts.find(c => c.id === contactId);
      if (contact) {
        if (incrementUnread !== undefined) {
          if (incrementUnread) contact.unread += 1;
        } else if (state.activeContactId !== contactId) {
          contact.unread += 1;
        }
      }
    },
    setTyping(state, action: PayloadAction<{ contactId: string; isTyping: boolean }>) {
      state.typing[action.payload.contactId] = action.payload.isTyping;
    },
    removeMessage(state, action: PayloadAction<string>) {
      const messageId = action.payload;
      for (const contactId in state.messages) {
        state.messages[contactId] = state.messages[contactId].filter(
          (m) => m.id !== messageId && m.id !== `local_${messageId}`
        );
      }
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // fetchChatContacts
    builder
      .addCase(fetchChatContacts.pending, state => { state.loading = true; state.error = null; })
      .addCase(fetchChatContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload.map((u: any) => ({
          id: u._id,
          _id: u._id,
          name: u.name,
          role: u.role || 'Employee',
          avatar: u.initials || getInitials(u.name),
          color: u.avColor || getColor(u.name),
          online: true,
          isGroup: !!u.isGroup,
          unread: u.unread ?? 0,
          lastMessage: u.lastMessage ?? '',
          lastMessageTime: u.lastMessageTime ?? null,
        }));
        // Set first contact active if none set
        if (!state.activeContactId && state.contacts.length > 0) {
          state.activeContactId = state.contacts[0].id;
        }
      })
      .addCase(fetchChatContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchMessages
    builder
      .addCase(fetchMessages.pending, state => { state.loadingMessages = true; })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;
        const { contactId, messages } = action.payload;
        // Get logged-in user id from stored session
        const stored = sessionStorage.getItem('hrms_user');
        let myId = '';
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            myId = parsed?.user?._id || parsed?._id || '';
          } catch { /* ignore */ }
        }

        state.messages[contactId] = messages.map((m: any) => {
          const senderId = m.sender?._id || m.sender;
          const isMe = senderId === myId;
          return {
            id: m._id,
            senderId: isMe ? 'me' : contactId,
            text: m.text,
            time: formatTime(m.createdAt),
            createdAt: m.createdAt,
            fileUrl: m.fileUrl,
            fileName: m.fileName,
            fileType: m.fileType,
          };
        });
      })
      .addCase(fetchMessages.rejected, state => { state.loadingMessages = false; });

    // sendMessageAsync
    builder
      .addCase(sendMessageAsync.fulfilled, (state, action) => {
        const m = action.payload;
        const contactId = m.receiver?._id || m.receiver;
        // Replace optimistic message with real server response
        if (state.messages[contactId]) {
          // Remove last optimistic message (id starts with 'local_')
          const lastOptIdx = [...state.messages[contactId]]
            .reverse()
            .findIndex(msg => msg.id.toString().startsWith('local_'));
          if (lastOptIdx !== -1) {
            const realIdx = state.messages[contactId].length - 1 - lastOptIdx;
            state.messages[contactId][realIdx] = {
              id: m._id,
              senderId: 'me',
              text: m.text,
              time: formatTime(m.createdAt),
              createdAt: m.createdAt,
              fileUrl: m.fileUrl,
              fileName: m.fileName,
              fileType: m.fileType,
            };
          }
        }
        // Update last message on contact
        const contact = state.contacts.find(c => c.id === contactId);
        if (contact) {
          contact.lastMessage = m.text;
          contact.lastMessageTime = m.createdAt;
        }
      });

    // markMessagesReadAsync
    builder
      .addCase(markMessagesReadAsync.fulfilled, (state, action) => {
        const contactId = action.payload;
        const c = state.contacts.find(c => c.id === contactId);
        if (c) c.unread = 0;
      });
  },
});

export const { setActiveContact, addLocalMessage, receiveMessage, setTyping, removeMessage, clearError } = chatSlice.actions;
export default chatSlice.reducer;
